import { SettingsManager } from "@viamap/viamap2-common";
import { JPBReferenceProperty } from "src/modules/JPBModule/JPBState";

type JPBForestHitSource = {
  id?: unknown;
  dokumentId?: unknown;
  documentID?: unknown;
  bfenummer?: unknown;
  bfeList?: unknown;
  areal?: unknown;
  m2total?: unknown;
  skovAreal?: unknown;
  pris?: unknown;
  dato?: unknown;
  type?: unknown;
  geopoint?: unknown;
  geoshape?: unknown;
  status?: unknown;
  error?: unknown;
  reasonCode?: unknown;
  reasonText?: unknown;
};

type ESTransactionHit<T> = {
  _id?: string;
  _source?: T;
  _seq_no?: number;
  sort?: any[];
};

function toFiniteNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toStringOrNull(value: unknown): string | null {
  if (typeof value === "string" && value.trim().length > 0) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function toEpochMilliseconds(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value > 1e12) return Math.floor(value);
    if (value > 1e9) return Math.floor(value * 1000);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) {
      if (asNumber > 1e12) return Math.floor(asNumber);
      if (asNumber > 1e9) return Math.floor(asNumber * 1000);
    }
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return Math.floor(parsed);
  }
  return 0;
}

function parseGeoPoint(geopoint: unknown): { lng: number; lat: number } {
  if (Array.isArray(geopoint) && geopoint.length >= 2) {
    return { lng: toFiniteNumber(geopoint[0]), lat: toFiniteNumber(geopoint[1]) };
  }
  if (geopoint && typeof geopoint === "object") {
    const pointObj = geopoint as { coordinates?: unknown; lon?: unknown; lat?: unknown };
    if (Array.isArray(pointObj.coordinates) && pointObj.coordinates.length >= 2) {
      return {
        lng: toFiniteNumber(pointObj.coordinates[0]),
        lat: toFiniteNumber(pointObj.coordinates[1]),
      };
    }
    return {
      lng: toFiniteNumber(pointObj.lon),
      lat: toFiniteNumber(pointObj.lat),
    };
  }
  return { lng: 0, lat: 0 };
}

function extractBfeNumber(source: JPBForestHitSource): string {
  const direct = toStringOrNull(source.bfenummer);
  if (direct) return direct;
  if (Array.isArray(source.bfeList) && source.bfeList.length > 0) {
    const first = toStringOrNull(source.bfeList[0]);
    if (first) return first;
  }
  return "";
}

export class JPBSkovDemoManager {
  private static readonly defaultTargetIndex = "jpb_skov_2026_05_beta";
  private static readonly pageSize = 200;
  private static readonly maxPages = 2000;
  private static readonly requestTimeoutMs = 30000;

  private static getJwtToken(): string | undefined {
    try {
      const authData = SettingsManager.getSystemSetting("authData");
      if (!authData || !authData.ClientId) {
        return undefined;
      }
      const lastAuthUserKey = `CognitoIdentityServiceProvider.${authData.ClientId}.LastAuthUser`;
      const username = localStorage.getItem(lastAuthUserKey);
      if (!username) {
        return undefined;
      }
      const idTokenKey = `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.idToken`;
      return localStorage.getItem(idTokenKey) || undefined;
    } catch {
      return undefined;
    }
  }

  private static mapHitToReferenceProperty(
    hit: ESTransactionHit<JPBForestHitSource>,
  ): JPBReferenceProperty {
    const source = hit._source || {};
    const documentID =
      toStringOrNull(source.id) || toStringOrNull(hit._id) || crypto.randomUUID();
    const dokumentId =
      toStringOrNull(source.dokumentId) ||
      toStringOrNull(source.documentID) ||
      documentID;
    const area = Math.max(
      0,
      toFiniteNumber(source.m2total) ||
        toFiniteNumber(source.areal) ||
        toFiniteNumber(source.skovAreal),
    );
    const price = Math.max(0, toFiniteNumber(source.pris));
    const pricePerSquareMeter = area > 0 ? price / area : 0;
    const dateMs = toEpochMilliseconds(source.dato);
    const point = parseGeoPoint(source.geopoint);
    /** Invalid Date when `dato` is missing or unparseable; UI formats as "-". */
    const transactionDate =
      dateMs > 0 ? new Date(dateMs) : new Date(Number.NaN);

    return {
      documentID,
      dokumentId,
      bfe_nr: extractBfeNumber(source),
      area,
      originalTotalPrice: price,
      totalPrice: price,
      transactionDate,
      pricePerSquareMeter,
      originalPricePerSquareMeter: pricePerSquareMeter,
      coordinates: {
        lat: point.lat,
        lng: point.lng,
      },
      geoshape: source.geoshape || null,
      address: "",
      transactionType: toStringOrNull(source.type) || "",
      transactionStatus: toStringOrNull(source.status) || "",
      error: toFiniteNumber(source.error),
      reasonCode: toStringOrNull(source.reasonCode),
      reasonText: toStringOrNull(source.reasonText),
      prisDriftsbygning: null,
      prisStuehus: null,
    };
  }

  public static async fetchReferenceProperties(options: {
    polygonCoordinates: number[][][];
    indexName?: string;
    abortSignal?: AbortSignal;
  }): Promise<JPBReferenceProperty[]> {
    const indexName =
      options.indexName ||
      SettingsManager.getSystemSetting(
        "jpb.indexNameSkov",
        this.defaultTargetIndex,
      );
    const noSQLDataUrl = SettingsManager.getSystemSetting("getNoSQLDataUrl");
    if (!noSQLDataUrl) {
      throw new Error("Missing getNoSQLDataUrl system setting.");
    }

    const isMultiPolygon =
      options.polygonCoordinates && options.polygonCoordinates.length > 1;
    const geoShapeType = isMultiPolygon ? "MultiPolygon" : "Polygon";
    const geoShapeCoordinates = isMultiPolygon
      ? options.polygonCoordinates.map((ring) => [ring])
      : options.polygonCoordinates;

    const baseQuery = {
      query: {
        bool: {
          filter: [
            {
              geo_shape: {
                geopoint: {
                  shape: {
                    type: geoShapeType,
                    coordinates: geoShapeCoordinates,
                  },
                  relation: "within",
                },
              },
            },
          ],
        },
      },
      sort: [{ _seq_no: "desc" }],
    };

    const allHits: ESTransactionHit<JPBForestHitSource>[] = [];
    let searchAfter: any[] | null = null;
    let hasMore = true;
    let currentPage = 0;
    const seenPageTokens = new Set<string>();

    while (hasMore && currentPage < this.maxPages) {
      if (options.abortSignal?.aborted) {
        throw new Error("Request aborted");
      }
      currentPage += 1;
      const pageToken = searchAfter ? JSON.stringify(searchAfter) : "__first_page__";
      if (seenPageTokens.has(pageToken)) {
        throw new Error("Pagination loop detected while fetching forest data.");
      }
      seenPageTokens.add(pageToken);

      const esQuery: any = {
        ...baseQuery,
        size: this.pageSize,
      };
      if (searchAfter) {
        esQuery.search_after = searchAfter;
      }

      const requestAbortController = new AbortController();
      const timeoutId = window.setTimeout(() => {
        requestAbortController.abort(
          new Error(
            `JPBSkovDemo request timed out after ${this.requestTimeoutMs}ms`,
          ),
        );
      }, this.requestTimeoutMs);
      const onAbort = () =>
        requestAbortController.abort(new Error("Request aborted"));
      options.abortSignal?.addEventListener("abort", onAbort, { once: true });

      let response: Response;
      try {
        response = await fetch(noSQLDataUrl, {
          method: "POST",
          signal: requestAbortController.signal,
          headers: {
            "content-type": "application/json",
            ...(this.getJwtToken() && {
              Authorization: `Bearer ${this.getJwtToken()}`,
            }),
          },
          body: JSON.stringify({
            index: `/${indexName}/_search`,
            query: esQuery,
            maxRows: this.pageSize,
            ...(searchAfter ? { searchAfter } : {}),
          }),
        });
      } finally {
        window.clearTimeout(timeoutId);
        options.abortSignal?.removeEventListener("abort", onAbort);
      }

      if (response.status !== 200) {
        const errorText = await response.text();
        throw new Error(
          `Fejl ved hentning af skovdata (${response.status}): ${errorText}`,
        );
      }

      const body = await response.json();
      const hits: ESTransactionHit<JPBForestHitSource>[] = body.hits?.hits || [];
      allHits.push(...hits);

      const pagination = body.pagination;
      if (pagination) {
        hasMore = Boolean(pagination.hasMore);
        searchAfter =
          Array.isArray(pagination.nextSearchAfter) &&
          pagination.nextSearchAfter.length > 0
            ? pagination.nextSearchAfter
            : null;
      } else {
        hasMore = hits.length === this.pageSize;
        const lastSort = hits.length > 0 ? hits[hits.length - 1]?.sort : null;
        searchAfter =
          Array.isArray(lastSort) && lastSort.length > 0 ? lastSort : null;
      }

      if (hits.length === 0) {
        hasMore = false;
      }
      if (hasMore && !searchAfter) {
        hasMore = false;
      }
    }

    return allHits.map((hit) => this.mapHitToReferenceProperty(hit));
  }
}
